import { apiGet, apiPost, apiPatch, apiPut } from './client';

export interface AdminTenant {
  id: number;
  name: string;
  code: string;
  tenantType: string;
  industry?: string;
  gstin?: string;
  drugLicenseNumber?: string;
  drugLicenseExpiry?: string;
  hasDlDocument: boolean;
  foodLicenseNumber?: string;
  foodLicenseExpiry?: string;
  hasFoodDocument: boolean;
  address?: string;
  city?: string;
  district?: string;
  state?: string;
  pincode?: string;
  phone?: string;
  email?: string;
  vendorName?: string;
  softwareVendorId?: number;
  status: string;
  createdAt: string;
}

export interface AdminTenantPage {
  content: AdminTenant[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

export interface SoftwareVendorPage {
  content: SoftwareVendor[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

export interface SoftwareVendor {
  id: number;
  companyName: string;
  softwareName: string;
  phone?: string;
  gstin?: string;
  city?: string;
  state?: string;
  industry?: string;
  createdAt: string;
  supplierCount: number;
  retailerCount: number;
  representativeCount: number;
}

export interface VendorStat {
  vendorName: string;
  total: number;
  wholesalerCount: number;
  retailerCount: number;
  representativeCount: number;
}

export interface AdminCreateTenantRequest {
  tenantName: string;
  tenantType: string;
  industry?: string;
  gstin?: string;
  vendorName?: string;
  adminUsername?: string;
  adminPassword?: string;
  address?: string;
  city?: string;
  district?: string;
  state?: string;
  pincode?: string;
  phone: string;
  tenantEmail?: string;
}

/** Scopes a tenant/invoice text search to a single field instead of the default OR-across-columns search. */
export type AdminTenantSearchField = 'name' | 'gst' | 'phone' | 'code';

/** Scopes a software-vendor text search to a single field. */
export type VendorSearchField = 'name' | 'city' | 'gst' | 'phone';

function buildQuery(params: Record<string, string | boolean | number | undefined>) {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '' && v !== null)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return parts.length ? '?' + parts.join('&') : '';
}

export const adminApi = {
  listTenants(params: {
    type?: string;
    q?: string;
    searchField?: AdminTenantSearchField;
    noGstin?: boolean;
    noLicense?: boolean;
    licenseExpired?: boolean;
    vendorName?: string;
    softwareVendorId?: number;
    industry?: string;
    page?: number;
    size?: number;
  }) {
    const sendParams = { ...params, searchField: params.q ? params.searchField : undefined };
    return apiGet<AdminTenantPage>(`/admin/tenants${buildQuery(sendParams as Record<string, string | boolean | number | undefined>)}`);
  },

  getTenant(id: number) {
    return apiGet<AdminTenant>(`/admin/tenants/${id}`);
  },

  createTenant(req: AdminCreateTenantRequest) {
    return apiPost<AdminTenant, AdminCreateTenantRequest>('/admin/tenants', req);
  },

  updateVendor(id: number, softwareVendorId: number | null) {
    return apiPatch<AdminTenant, { softwareVendorId: number | null }>(
      `/admin/tenants/${id}/vendor`, { softwareVendorId });
  },

  vendorStats() {
    return apiGet<VendorStat[]>('/admin/tenants/vendor-stats');
  },

  /**
   * Paginated by default (page/size, default size 50) for the Vendors table.
   * Callers that need the full list for a dropdown (e.g. the SW Vendor filter
   * on Registered Users / Subscription) should pass a large `size` instead
   * of paging through — vendor counts are expected to stay well under that.
   */
  listSoftwareVendors(params?: { q?: string; searchField?: VendorSearchField; page?: number; size?: number }) {
    const sendParams = {
      q: params?.q,
      searchField: params?.q ? params?.searchField : undefined,
      page: params?.page,
      size: params?.size,
    };
    return apiGet<SoftwareVendorPage>(`/admin/software-vendors${buildQuery(sendParams)}`);
  },

  createSoftwareVendor(req: {
    companyName: string; softwareName: string;
    phone?: string; gstin?: string; city?: string; state?: string; industry?: string;
  }) {
    return apiPost<SoftwareVendor, typeof req>('/admin/software-vendors', req);
  },

  updateSoftwareVendor(id: number, req: {
    companyName?: string; softwareName?: string;
    phone?: string; gstin?: string; city?: string; state?: string; industry?: string;
  }) {
    return apiPatch<SoftwareVendor, typeof req>(`/admin/software-vendors/${id}`, req);
  },

  /** POST /admin/tenants/{id}/change-password — super-admin force-resets any tenant's password. */
  changePassword(tenantId: number, newPassword: string) {
    return apiPost<void, { newPassword: string }>(
      `/admin/tenants/${tenantId}/change-password`,
      { newPassword },
    );
  },

  /** GET /admin/tenants/{id}/api-key — view the current API key for a tenant. */
  getTenantApiKey(tenantId: number) {
    return apiGet<{ apiKey: string }>(`/admin/tenants/${tenantId}/api-key`);
  },

  /** POST /admin/tenants/{id}/regenerate-api-key — regenerate tenant API key (requires super-admin password). */
  regenerateTenantApiKey(tenantId: number, superAdminPassword: string) {
    return apiPost<{ apiKey: string }, { superAdminPassword: string }>(
      `/admin/tenants/${tenantId}/regenerate-api-key`,
      { superAdminPassword },
    );
  },
};

export interface SystemSetting {
  key: string;
  value: string;
  updatedAt: string | null;
}

export const adminSettingsApi = {
  getAll() {
    return apiGet<SystemSetting[]>('/admin/settings');
  },
  update(key: string, value: string) {
    return apiPut<SystemSetting, { value: string }>(`/admin/settings/${key}`, { value });
  },
};
